import { Typography } from "@mui/material";
import GGButton from "../../components/CommonComponents/GGButton/GGButton";
import "./SubmitFinalWorkPage2.scss";

import { useEffect, useRef, useState } from "react";
import GGDialogueBox, { dialogueType } from "../../components/UtilityComponents/GGDialogueBox/GGDialogueBox";
import { Order } from "../../Models/Order";
import { UploadableImage } from "../../components/UtilityComponents/UploadImagesSection/UploadImagesSection";
import imageCompression from "browser-image-compression";
import { addWatermarkToImage } from "../../utils/WatermarkUtil";
import { DeliveryAPIHandler } from "../../services/NetworkClient/BackendAPIs/DeliveryAPIHandler";
import CustomMessageDeliveryHandler from "../../services/ChatManager/CustomMessageDeliveryHandler";
import GGLoaderButton from "../../components/CommonComponents/GGLoaderButton/GGLoaderButton";
import SellerWorkspaceFeedbackBlock from "./Subviews/SellerWorkspaceFeedbackBlock";
import { ErrorLogger } from "../../utils/Logger";
import { getUploadErrorMessage, ResumableUploadTask } from "../../services/UploadClient/ResumableUploadTask";
import { UploadPurpose } from "../../services/NetworkClient/BackendAPIs/UploadAPIHandler";

export interface SubmitFinalWorkPage2Props {
    prevPage: () => void;
    filesToUpload: File[];
    previewImagesToUpload: UploadableImage[];
    order: Order | null;
    remarks: string;
    onDeliverySuccess: () => void;
    closeModal: () => void;
}

const SubmitFinalWorkPage2 = (props: SubmitFinalWorkPage2Props) => {

    const [pvwImagesProgress, setPvwImagesProgress] = useState<number>(0);
    const [totalProgress, setTotalProgress] = useState<number>(0);
    const [filesUploadProgress, setFilesUploadProgress] = useState<number>(0);
    const [pvwImageAssetIds, setPvwImageAssetIds] = useState<string[]>([]);
    const [originalFileAssetIds, setOriginalFileAssetIds] = useState<string[]>([]);
    const uploadTasks = useRef<ResumableUploadTask[]>([]);
    const [showAbortDialogBox, setShowAbortDialogBox] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>();
    const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
    const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

    useEffect(() => {
      const tasks = uploadTasks.current;
      const pvwFiles: File[] = props.previewImagesToUpload
        .map((uploadableFile) => uploadableFile.file)
        .filter((file): file is File => file !== undefined);
      

      // @TODO: Move this funciton to utils - createa a new util named ImageCompressionUtil
      const compressImages = async () => {
        try {
          const compressedFiles: File[] = await Promise.all(
            pvwFiles.map(async (file) => {

              const options = {
                maxSizeMB: 1,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: "image/jpeg",
                initialQuality: 0.5,
              };
      
              const compressedFile = await imageCompression(file, options);
      
              const watermarkedBlob = await addWatermarkToImage(
                compressedFile,
                "GenieGig © 2025 (PlatinumSpark Technologies Pvt Ltd)"
              );

              const jpegFileName = file.name.replace(/\.[^/.]+$/, ".jpg");
      
              const watermarkedFile = new File([watermarkedBlob], jpegFileName, {
                type: "image/jpeg",
              });

              return watermarkedFile;
            })
          );
      
      
          pvwFiles.length = 0;
          pvwFiles.push(...compressedFiles);

        } catch (error) {
          ErrorLogger.log(error, 'SubmitFinalWorkPage2.compressImages');
          throw error;
        }
      };

      const uploadFiles = async (files: File[], purpose: UploadPurpose, setProgress: (progress: number) => void) => {
        if (files.length === 0) {
          setProgress(100);
          return [];
        }
        const percentages = new Array(files.length).fill(0);
        const assets = await Promise.all(files.map((file, index) => {
          const task = new ResumableUploadTask(file, {
            purpose,
            resourceId: props.order?.id,
            onProgress: (progress) => {
              percentages[index] = progress.percentage;
              setProgress(percentages.reduce((total, value) => total + value, 0) / percentages.length);
            },
          });
          tasks.push(task);
          return task.start();
        }));
        return assets.map((asset) => asset.assetId);
      };

      const uploadDelivery = async () => {
        try {
          const previewUpload = compressImages().then(() =>
            uploadFiles(pvwFiles, UploadPurpose.ORDER_DELIVERY_PREVIEW, setPvwImagesProgress),
          );
          const originalUpload = uploadFiles(props.filesToUpload, UploadPurpose.ORDER_DELIVERY_ORIGINAL, setFilesUploadProgress);
          const [previewIds, originalIds] = await Promise.all([previewUpload, originalUpload]);
          setPvwImageAssetIds(previewIds);
          setOriginalFileAssetIds(originalIds);
        } catch (error) {
          ErrorLogger.log(error, 'SubmitFinalWorkPage2.uploadDelivery');
          setErrorMessage(stringifyError(error));
          setShowErrorDialog(true);
        }
      };

      uploadDelivery();
      return () => { tasks.forEach((task) => { task.cancel().catch(() => {}); }); };
//eslint-disable-next-line
    }, []);

    useEffect(() => {
      setTotalProgress((pvwImagesProgress + filesUploadProgress) / 2);
    }, [pvwImagesProgress, filesUploadProgress]);

    function stringifyError(error: any): string {
      if (error instanceof Error) {
        // const errorCode = (error as any).code ? ` (${(error as any).code})` : "";
        return `${error.name}: ${error.message}`;
      } else {
        return `Error: ${String(error)}`;
      }
  }
    
  const submitButtonHandler = async () => {
      if (!props.order) {
          setErrorMessage("Order not found.");
          setShowErrorDialog(true);
          return;
      }

      try {
          setIsSubmitLoading(true);
          // Prepare deliverables array
          const deliverables = props.filesToUpload.map((file, idx) => ({
              fileName: file.name,
              fileType: file.type,
              fileSizeInBytes: file.size,
              mediaAssetId: originalFileAssetIds[idx] || "",
          }));

          // Prepare delivery data
          const deliveryData = {
              order: props.order.id,
              previewAssetIds: pvwImageAssetIds,
              deliverables,
              remarks: props.remarks,
          };

          await DeliveryAPIHandler.shared.createDelivery(deliveryData, props.order.id);
          const customMessageDeliveryHandler = new CustomMessageDeliveryHandler(props.order.metaData.channelId);
          await customMessageDeliveryHandler.sendOrderSubmittedBySellerCustomMessage(props.order.id, {
            buyerId: props.order.metaData.buyer.id,
            sellerId: props.order.metaData.seller.id
          });
          
          // Refresh orders in parent and close modal
          setIsSubmitLoading(false);
          props.onDeliverySuccess();
          props.closeModal();

      } catch (error: any) {
          setIsSubmitLoading(false);
          setErrorMessage(getUploadErrorMessage(error));
          setShowErrorDialog(true);
      }
  }

    const abortUploadProgress = () => {
      uploadTasks.current.forEach((task) => { task.cancel().catch(() => {}); });
    props.prevPage();
  };

    const cancelButtonHandler = () => {
        setShowAbortDialogBox(true);
    }

  return (
    <>

    <GGDialogueBox type={dialogueType.Error} successHandler={abortUploadProgress} isOpen={showErrorDialog} message={errorMessage || "Something went wrong, please try again"} closeHandler={() => { setShowErrorDialog(false); props.prevPage(); }} />
  
      <div className="submit-final-work-modal-page-2-content">
        <div className="progress-bar-section">
          <Typography className="uploading-files-heading">
            Uploading Files...
          </Typography>
          <progress className="progress-bar" value={totalProgress} max={100}></progress>
          <h4>{totalProgress}%</h4>
        </div>

        <SellerWorkspaceFeedbackBlock />

        <div className="buttons-bar">
          <GGButton className="button cancel-btn" color="error" disableClickShrink variant="outlined" onClick={cancelButtonHandler} > Cancel </GGButton>
            <GGDialogueBox type={dialogueType.Alert} successHandler={abortUploadProgress} isOpen={showAbortDialogBox} message='Are you sure to **ABORT** the file upload progress? All the progress will be lost' closeHandler={() => { setShowAbortDialogBox(false) }} />
          <GGLoaderButton isLoading={isSubmitLoading} className="submit-btn button" color="success" variant="contained" onClick={submitButtonHandler} disabled={totalProgress !== 100}> Submit Final Work </GGLoaderButton>
        </div>
      </div>
    </>
  );
};

export default SubmitFinalWorkPage2;

